import React, { useState, useRef, useEffect } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAY_NAMES = ["Su","Mo","Tu","We","Th","Fr","Sa"];
const _d = new Date();
const TODAY = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, "0")}-${String(_d.getDate()).padStart(2, "0")}`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function addMonths(year: number, month: number, n: number) {
  const d = new Date(year, month + n, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}
function isoOf(year: number, month: number, day: number) {
  return new Date(year, month, day).toISOString().slice(0, 10);
}
function formatShort(iso: string) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
    day: "numeric", month: "short",
  });
}
// minimum: 89 days back from TODAY
const MIN_DATE = (() => {
  const d = new Date(TODAY);
  d.setDate(d.getDate() - 89);
  return d.toISOString().slice(0, 10);
})();

// ─── Band colour ──────────────────────────────────────────────────────────────
const BAND_BG = "rgb(236,254,255)"; // cyan-50

// ─── Props ────────────────────────────────────────────────────────────────────
interface Props {
  from: string; // "YYYY-MM-DD" or ""
  to:   string;
  onChange: (from: string, to: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────
const DateRangePicker: React.FC<Props> = ({ from, to, onChange }) => {
  const [open,      setOpen]      = useState(false);
  const [selecting, setSelecting] = useState<string | null>(null); // first click
  const [hovered,   setHovered]   = useState<string | null>(null);

  // Left month starts at (current − 1) so right = current month
  const [left, setLeft] = useState(() =>
    addMonths(new Date(TODAY).getFullYear(), new Date(TODAY).getMonth(), -1)
  );
  const right = addMonths(left.year, left.month, 1);

  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSelecting(null);
        setHovered(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // ── Range to highlight (live or confirmed) ──────────────────────────────────
  const hlFrom = selecting ?? from;
  const hlTo   = selecting ? (hovered ?? selecting) : to;
  const [lo, hi] =
    hlFrom && hlTo
      ? hlFrom <= hlTo ? [hlFrom, hlTo] : [hlTo, hlFrom]
      : [hlFrom, hlFrom];

  // ── Day click ───────────────────────────────────────────────────────────────
  const onDayClick = (iso: string) => {
    if (iso < MIN_DATE || iso > TODAY) return;
    if (!selecting) {
      setSelecting(iso);
      setHovered(null);
    } else {
      const [f, t] = iso >= selecting ? [selecting, iso] : [iso, selecting];
      onChange(f, t);
      setSelecting(null);
      setHovered(null);
      setOpen(false);
    }
  };

  // ── Navigation guard ────────────────────────────────────────────────────────
  const canGoNext = () => {
    const maxLeft = addMonths(
      new Date(TODAY).getFullYear(), new Date(TODAY).getMonth(), -1
    );
    return (
      left.year < maxLeft.year ||
      (left.year === maxLeft.year && left.month < maxLeft.month)
    );
  };

  // ── Render one calendar month ───────────────────────────────────────────────
  const renderMonth = (year: number, month: number) => {
    const firstDow    = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: (string | null)[] = Array(firstDow).fill(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(isoOf(year, month, d));

    return (
      <div className="flex-1 min-w-0">
        {/* Month title */}
        <p className="text-sm font-semibold text-slate-800 text-center mb-3">
          {MONTH_NAMES[month]} {year}
        </p>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 mb-0.5">
          {DAY_NAMES.map(d => (
            <div key={d} className="text-center text-[11px] font-medium text-slate-400 py-1">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {cells.map((iso, i) => {
            if (!iso) return <div key={`empty-${i}`} className="h-9" />;

            const day      = new Date(iso + "T00:00:00").getDate();
            const disabled = iso < MIN_DATE || iso > TODAY;
            const isLo     = iso === lo && lo !== hi;
            const isHi     = iso === hi && lo !== hi;
            const isOnly   = iso === lo && lo === hi && !!lo;
            const inBand   = !!lo && !!hi && iso > lo && iso < hi;
            const isToday  = iso === TODAY;

            // Wrapper inline style for half-band on start/end edges
            let wrapStyle: React.CSSProperties = {};
            if (!disabled) {
              if (inBand)  wrapStyle = { background: BAND_BG };
              else if (isLo && !isOnly) wrapStyle = { background: `linear-gradient(to right, transparent 50%, ${BAND_BG} 50%)` };
              else if (isHi && !isOnly) wrapStyle = { background: `linear-gradient(to left,  transparent 50%, ${BAND_BG} 50%)` };
            }

            // Button class
            let btnCls =
              "w-8 h-8 flex items-center justify-center text-xs font-medium rounded-full mx-auto transition-colors select-none";
            if (disabled) {
              btnCls += " text-slate-300 cursor-not-allowed";
            } else if (isLo || isHi || isOnly) {
              btnCls += " bg-slate-900 text-white cursor-pointer";
            } else if (isToday) {
              btnCls += " text-cyan-600 font-bold hover:bg-slate-100 cursor-pointer ring-1 ring-cyan-300";
            } else {
              btnCls += " text-slate-700 hover:bg-slate-100 cursor-pointer";
            }

            return (
              <div
                key={iso}
                className="h-9 flex items-center"
                style={wrapStyle}
              >
                <button
                  onClick={() => onDayClick(iso)}
                  onMouseEnter={() => selecting && !disabled && setHovered(iso)}
                  onMouseLeave={() => selecting && setHovered(null)}
                  disabled={disabled}
                  className={btnCls}
                >
                  {day}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Trigger label ───────────────────────────────────────────────────────────
  const label =
    from && to
      ? `${formatShort(from)} – ${formatShort(to)}`
      : selecting
      ? `${formatShort(selecting)} – ?`
      : "Select range";

  const hasRange = from && to && !selecting;

  return (
    <div ref={wrapRef} className="relative">
      {/* ── Trigger button ── */}
      <button
        onClick={() => {
          setOpen(o => !o);
          if (!open) { setSelecting(null); setHovered(null); }
        }}
        className={`flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-xl border transition-all ${
          hasRange
            ? "border-cyan-400 bg-cyan-50 text-cyan-700"
            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
        }`}
      >
        {/* Calendar icon */}
        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>

        <span>{label}</span>

        {/* Clear ×  */}
        {hasRange && (
          <span
            role="button"
            onClick={e => { e.stopPropagation(); onChange("", ""); }}
            className="ml-0.5 text-base leading-none text-slate-400 hover:text-slate-700"
          >
            ×
          </span>
        )}
      </button>

      {/* ── Calendar popup ── */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-[580px] bg-white border border-slate-100 rounded-2xl shadow-2xl z-50 p-6">

          {/* Month navigation */}
          <div className="flex items-center justify-between mb-5">
            <button
              onClick={() => setLeft(addMonths(left.year, left.month, -1))}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => canGoNext() && setLeft(addMonths(left.year, left.month, 1))}
              className={`p-1.5 rounded-lg text-slate-500 transition-colors ${canGoNext() ? "hover:bg-slate-100" : "opacity-25 cursor-not-allowed"}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Two-month grid */}
          <div className="flex gap-6">
            {renderMonth(left.year, left.month)}
            <div className="w-px bg-slate-100 self-stretch flex-shrink-0" />
            {renderMonth(right.year, right.month)}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-5 mt-4">
            <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <span className="w-3.5 h-3.5 rounded-full bg-slate-900 inline-block flex-shrink-0" />
              Selected
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <span className="w-3.5 h-3.5 rounded-full ring-1 ring-cyan-300 inline-block flex-shrink-0" />
              Today
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <span className="w-5 h-3 rounded-sm inline-block flex-shrink-0" style={{ background: BAND_BG, border: "1px solid #a5f3fc" }} />
              Range
            </span>
          </div>

          {/* Footer hint */}
          <p className="text-[11px] text-slate-400 text-center mt-2 tracking-wide">
            {selecting ? "Now select an end date" : "Select a start date"}
          </p>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
